import { useState, useEffect, useCallback } from 'react';

import { ShieldCheck, Mail, Upload, PhoneCall, RefreshCw, Lock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/usePageTitle';
import { authApi } from '@/lib/api/auth.api';
import { extractErrorMessage } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useBrandingStore } from '@/stores/branding.store';

import type { FormEvent } from 'react';

const RESEND_COOLDOWN_SECONDS = 30;

export const LoginPage = () => {
  usePageTitle('Login');
  const { companyName, logoUrl } = useBrandingStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [method, setMethod] = useState<'otp' | 'password'>('otp');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const {
    requestCode,
    verifyCode,
    loginWithPassword,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      const from = (location.state as { from?: Location })?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await requestCode(email);
      setStep('code');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await verifyCode(email, code);
      const from = (location.state as { from?: Location })?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await loginWithPassword(email, password);
      const from = (location.state as { from?: Location })?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) return;
    setError(null);

    try {
      await authApi.resendCode(email);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Something went wrong. Please try again.'));
    }
  }, [email, resendCooldown]);

  if (authLoading || isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-muted flex min-h-screen flex-col lg:flex-row">
      <aside className="bg-primary text-primary-foreground relative hidden lg:flex lg:w-1/2">
        <div className="bg-primary/90 absolute inset-0" />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div>
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt={companyName} className="h-12" />
              ) : (
                <ShieldCheck className="h-10 w-10" />
              )}
              <div>
                <p className="text-sm font-semibold tracking-wide uppercase">{companyName}</p>
                <p className="text-lg font-bold">Ai VoiceOps platform</p>
              </div>
            </div>
            <p className="text-primary-foreground/90 mt-8 text-lg">
              Intelligent inbound and outbound AI calling system to capture customer data, qualify
              leads, and automate follow-ups. Every interaction secured and audited.
            </p>
          </div>

          <div className="text-primary-foreground/90 space-y-6">
            <div className="flex items-start gap-3">
              <Upload className="mt-1 h-5 w-5" />
              <div>
                <p className="font-semibold">Contact Management</p>
                <p className="text-sm">
                  Import contact lists, configure calling campaigns, and manage customer data.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <PhoneCall className="mt-1 h-5 w-5" />
              <div>
                <p className="font-semibold">AI-Powered Calls</p>
                <p className="text-sm">
                  Intelligent AI agents handle inbound and outbound calls, capturing structured data
                  automatically.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <RefreshCw className="mt-1 h-5 w-5" />
              <div>
                <p className="font-semibold">Smart Automation</p>
                <p className="text-sm">
                  Automated notifications, follow-ups, and retry logic ensure no lead is left
                  behind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="space-y-2">
            <CardTitle className="text-3xl">Sign in</CardTitle>
            <div className="bg-muted inline-flex w-full rounded-md p-1">
              <button
                type="button"
                className={`rounded-sm px-3 py-1 text-sm font-medium transition ${
                  method === 'otp'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
                onClick={() => {
                  setMethod('otp');
                  setStep('email');
                  setCode('');
                  setPassword('');
                  setError(null);
                }}
              >
                Login with code
              </button>
              <button
                type="button"
                className={`rounded-sm px-3 py-1 text-sm font-medium transition ${
                  method === 'password'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
                onClick={() => {
                  setMethod('password');
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
              >
                Login with password
              </button>
            </div>
            <CardDescription>
              {method === 'password'
                ? 'Enter your work email and password to sign in.'
                : step === 'email'
                  ? 'Enter your work email to receive a login code.'
                  : `We sent a code to ${email}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {method === 'password' ? (
              <form className="space-y-6" onSubmit={handlePasswordSubmit}>
                <div className="space-y-2">
                  <label htmlFor="email-password" className="text-foreground text-sm font-medium">
                    Work email
                  </label>
                  <div className="relative">
                    <Mail className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <input
                      id="email-password"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="admin@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-10 py-2 text-sm outline-none focus-visible:ring-2"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-foreground text-sm font-medium">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-10 py-2 text-sm outline-none focus-visible:ring-2"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            ) : step === 'email' ? (
              <form className="space-y-6" onSubmit={handleEmailSubmit}>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-foreground text-sm font-medium">
                    Work email
                  </label>
                  <div className="relative">
                    <Mail className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="admin@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-10 py-2 text-sm outline-none focus-visible:ring-2"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending code...' : 'Continue'}
                </Button>
              </form>
            ) : (
              <form className="space-y-6" onSubmit={handleCodeSubmit}>
                <div className="space-y-2">
                  <label htmlFor="code" className="text-foreground text-sm font-medium">
                    Login code
                  </label>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    maxLength={8}
                    required
                    autoComplete="one-time-code"
                    placeholder="Paste your code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.slice(0, 8))}
                    className="border-input bg-background focus-visible:ring-primary w-full rounded-md border px-4 py-2 text-center font-mono text-lg tracking-widest outline-none focus-visible:ring-2"
                  />
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify code'}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setCode('');
                      setError(null);
                    }}
                    className="text-primary hover:underline"
                  >
                    Change email
                  </button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0}
                    className="text-primary hover:underline disabled:no-underline disabled:opacity-50"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
